import { applyToPoint, inverse } from 'transformation-matrix';
import { MOBILE_ANNOUNCEMENT_BAR_PART_FROM_VIEWPORT } from '../constants/SIZES';
import {
  TGetAdjustedCoordsToSchemeMoveArgs,
  TGetAnimationTranslates,
  TGetCorrectedPageCoords,
  TGetTranslateCanvasCoords,
  TToScreenCoordsArgs,
} from '../types/helpers/coordinates';
import { isMobileLayout } from './layout';

function getDistance(x1, y1, x2, y2) {
  const dx = Math.abs(x2 - x1);
  const dy = Math.abs(y2 - y1);

  return Math.sqrt(dx * dx + dy * dy);
}

function getMiddlePoint(x1, y1, x2, y2) {
  return {
    x: Math.round((x2 + x1) / 2),
    y: Math.round((y2 + y1) / 2),
  };
}

function convertDegreesToRadians(degrees) {
  return (degrees / 180) * Math.PI;
}

function convertRadiansToDegrees(radians) {
  return (radians * 180) / Math.PI;
}

function getAngleBetweenPoints({ x1, x2, y1, y2 }) {
  let angle = null;

  if (x1 === x2 && y1 === y2) {
    angle = Math.PI / 2;
  } else {
    angle = Math.atan2(y2 - y1, x2 - x1);
  }

  return convertDegreesToRadians(90 + convertRadiansToDegrees(angle));
}

function movePointAtAngle({ point, angle, distance }) {
  return {
    x: point.x + Math.sin(angle) * distance,
    y: point.y - Math.cos(angle) * distance,
  };
}

function findExtremeCoordinates(data) {
  let { minX, minY, maxX, maxY } = data.reduce(
    (acc, seat) => {
      acc.minY = Math.min(acc.minY, seat.y);
      acc.minX = Math.min(acc.minX, seat.x);
      acc.maxY = Math.max(acc.maxY, seat.y);
      acc.maxX = Math.max(acc.maxX, seat.x);

      return acc;
    },
    {
      minX: null,
      minY: null,
      maxY: null,
      maxX: null,
    },
  );

  minX -= 15;
  maxX += 15;
  minY -= 15;
  maxY += 15;

  return {
    minX,
    maxX,
    minY,
    maxY,
  };
}

function getAdjustedCoordsToSchemeMove({
  node,
  xCoord,
  yCoord,
  scale = window.Widget.zoomController.currentZoom ||
    window.Widget.zoomController.currentCustomZoom,
}: TGetAdjustedCoordsToSchemeMoveArgs) {
  const { width: rootWidth, height: rootHeight } = window.Widget;

  const {
    dpr,
    offsetLeft,
    offsetTop,
    schemeParameters: { schemeHeight, schemeWidth },
  } = window.Widget;
  const { width, height, left, top } = getBoundingClientRect(node);

  const widthCoeff = schemeWidth / width;
  const heightCoeff = schemeHeight / height;

  const adjustedXCoord = ((left - xCoord) * widthCoeff * scale) / dpr;
  const adjustedyCoord = ((top - yCoord) * heightCoeff * scale) / dpr;

  const correctedInnerHeight = isMobileLayout()
    ? rootHeight * MOBILE_ANNOUNCEMENT_BAR_PART_FROM_VIEWPORT
    : rootHeight;

  const correctedWindowInnerWidth = ((rootWidth - offsetLeft) * dpr) / 2;
  const correctedWindowInnerHeight = ((correctedInnerHeight - offsetTop) * dpr) / 2;

  return {
    x: adjustedXCoord + correctedWindowInnerWidth,
    y: adjustedyCoord + correctedWindowInnerHeight,
  };
}

function toScreenCoords({ x, y, canvasEl }: TToScreenCoordsArgs) {
  const matrix = canvasEl.getContext('2d').getTransform();
  const { e: tx, f: ty, a, d } = matrix;
  const { left, top } = getBoundingClientRect(canvasEl);

  const wx = x * (a / window.Widget.dpr) + left + tx / window.Widget.dpr;
  const wy = y * (d / window.Widget.dpr) + top + ty / window.Widget.dpr;

  return {
    x: wx,
    y: wy,
  };
}

function getCorrectedPageCoords({
  xCoord,
  yCoord,
  schemeParameters,
  matrix,
  dpr,
}: TGetCorrectedPageCoords) {
  const { e: tx, f: ty, a } = matrix;
  const scale = Number(a.toFixed(5));
  const {
    hallLayoutMeta: { width: hallLayoutWidth, height: hallLayoutHeight },
  } = schemeParameters;

  let correctedX = xCoord * dpr;
  let correctedY = yCoord * dpr;

  if (correctedX < tx) {
    correctedX = tx;
  }

  if (correctedX > tx + hallLayoutWidth * scale) {
    correctedX = tx + hallLayoutWidth * scale;
  }

  if (correctedY < ty) {
    correctedY = ty;
  }

  if (correctedY > ty + hallLayoutHeight * scale) {
    correctedY = ty + hallLayoutHeight * scale;
  }

  return {
    correctedX,
    correctedY,
  };
}

function getTranslateCanvasCoords({
  matrix,
  correctedX,
  correctedY,
  newScale,
  windowSizes,
  dpr,
}: TGetTranslateCanvasCoords) {
  const { x, y } = applyToPoint(inverse(matrix), {
    x: correctedX,
    y: correctedY,
  });

  const newCanvasX = -x * newScale + (windowSizes.width * dpr) / 2;
  const newCanvasY = -y * newScale + (windowSizes.height * dpr) / 2;

  return {
    newCanvasX,
    newCanvasY,
  };
}

function getAnimationTranslates({ x, y, nodeScale, windowSizes }: TGetAnimationTranslates) {
  const translateX = -(x - windowSizes.width / 2) / nodeScale;
  const translateY = -(y - windowSizes.height / 2) / nodeScale;

  return {
    translateX,
    translateY,
  };
}

const getBoundingClientRect = (element: HTMLElement | SVGElement) => {
  const { bottom, height, left, right, top, width, x, y, toJSON } = element.getBoundingClientRect();

  return {
    ...element.getBoundingClientRect(),
    top: top + window.scrollY,
    left,
    right,
    bottom,
    x,
    y,
    width,
    height,
    toJSON,
  } as DOMRect;
};

export {
  getDistance,
  getMiddlePoint,
  convertDegreesToRadians,
  convertRadiansToDegrees,
  getAngleBetweenPoints,
  movePointAtAngle,
  findExtremeCoordinates,
  toScreenCoords,
  getAdjustedCoordsToSchemeMove,
  getCorrectedPageCoords,
  getTranslateCanvasCoords,
  getAnimationTranslates,
  getBoundingClientRect,
};
